import React, { useState } from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  CircularProgress,
  Chip
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useFetchBullMQJobs } from "./hooks/useFetchBullMQJobs";
import JobDetailsModal from "./JobDetailsModal";

const JobsList = ({ queueName }) => {
  const [status, setStatus] = useState("waiting");
  const [selectedJob, setSelectedJob] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isLoading, error } = useFetchBullMQJobs({
    queueName,
    status
  });

  const formatTimestamp = (timestamp) => {
    if (!timestamp) { return "N/A"; }
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (start, end) => {
    if (!start || !end) { return "N/A"; }
    const duration = end - start;
    return `${Math.round(duration / 1000)}s`;
  };

  const getJobStatusColor = (job) => {
    if (job.failedReason) { return "error"; }
    if (job.finishedOn) { return "success"; }
    if (job.processedOn) { return "info"; }
    return "default";
  };

  const columns = [
    {
      field: "id",
      headerName: "Job ID",
      width: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            cursor: "pointer",
            color: "primary.main",
            textDecoration: "underline"
          }}
          onClick={() => {
            setSelectedJob(params.row);
            setModalOpen(true);
          }}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: "name",
      headerName: "Job Name",
      width: 200
    },
    {
      field: "timestamp",
      headerName: "Created",
      width: 150,
      renderCell: (params) => formatTimestamp(params.value)
    },
    {
      field: "processedOn",
      headerName: "Started",
      width: 150,
      renderCell: (params) => formatTimestamp(params.value)
    },
    {
      field: "finishedOn",
      headerName: "Finished",
      width: 150,
      renderCell: (params) => formatTimestamp(params.value)
    },
    {
      field: "duration",
      headerName: "Duration",
      width: 100,
      renderCell: (params) => formatDuration(params.row.processedOn, params.row.finishedOn)
    },
    {
      field: "attemptsMade",
      headerName: "Attempts",
      width: 100
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.failedReason ? "Failed" : params.row.finishedOn ? "Completed" : params.row.processedOn ? "Processing" : "Waiting"}
          color={getJobStatusColor(params.row)}
          size="small"
        />
      )
    }
  ];

  const jobs = data?.jobs || [];

  if (!queueName) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="text.secondary">
          Select a queue to view jobs
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Typography variant="h6">
          Jobs for {queueName}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="waiting">Waiting</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {isLoading
        ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        )
        : error
          ? (
            <Typography color="error">Error loading jobs: {error.message}</Typography>
          )
          : (
            <Box height="500px">
              <DataGrid
                rows={jobs}
                columns={columns}
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                disableSelectionOnClick
                getRowId={(row) => row.id}
              />
            </Box>
          )}

      <JobDetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        job={selectedJob}
        queueName={queueName}
      />
    </Box>
  );
};

export default JobsList;
